import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movieData';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = heroSlides[currentSlide];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <img
        src={slide.imageUrl}
        alt={slide.title}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75"></div>

      <div className="absolute inset-0 flex items-end md:items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-[1280px] mx-auto">
            <div
              key={currentSlide}
              className="animate-[fadeInUp_0.6s_ease-out] max-w-2xl pb-10 md:pb-0"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                  {slide.badge}
                </span>
                <span className="text-background-50/80 text-sm">{slide.category}</span>
                <span className="flex items-center gap-1 text-accent-400 text-sm font-semibold">
                  <i className="ri-star-fill text-xs"></i>
                  {slide.rating}
                </span>
              </div>

              <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">
                {slide.title}
              </h2>
              <p className="text-background-50/80 text-sm md:text-base mb-1">
                {slide.subtitle}
              </p>
              <p className="text-background-50/60 text-xs md:text-sm leading-relaxed mb-5 max-w-xl line-clamp-2 md:line-clamp-3">
                {slide.description}
              </p>

              <Link
                to={slide.link}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
              >
                查看详情
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide
                ? 'bg-primary-500 w-6'
                : 'bg-background-50/40 hover:bg-background-50/70'
            }`}
          />
        ))}
      </div>

      <button
        onClick={() => goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur-sm text-background-50 transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur-sm text-background-50 transition-all cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}